import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDisplayErrorMessage } from '@/lib/api';
import { draftOutreach, getOutreach, redraftLabel, sourcePath, type OutreachView } from '@/lib/propertyOutreach';
import { OutreachEditor, OutreachRecord } from '@/components/ui/ContactPropertyCard';
import formStyles from '@/components/ui/Form.module.css';
import styles from '@/components/ui/ContactProperty.module.css';

/**
 * One draft to a property (Warren, 4 Nov 2026, ruling R12): the page EVI ops' emails and the EVI queues link to, for the
 * ladder's breaker step and its follow-ups. EVI edits and sends it, or drops it. A breaker step that was dropped, or that
 * reached only some of its people, can be drafted again while its ask is open (can.redraft, the Api's flag). The Api answers
 * 403 outside EVI staff, and this page then says so and shows nothing else.
 */
export function PropertyOutreachPage({ outreachId, onNavigate }: { outreachId: string; onNavigate: (to: string) => void }) {
  const client = useQueryClient();
  const key = ['property-outreach', outreachId];
  const q = useQuery({ queryKey: key, queryFn: () => getOutreach(outreachId), retry: false });
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const update = (next: OutreachView) => {
    client.setQueryData(key, next);
    void client.invalidateQueries({ queryKey: ['evi-queues'] });
  };
  if (q.isError) return <div className={styles.page}><p className={styles.hint} role="alert">This draft is not available. Contacting a property is for the EV Initiative team.</p></div>;
  if (!q.data) return <div className={styles.page}><p className={styles.hint} role="status">Loading the draft</p></div>;
  const d = q.data, back = sourcePath(d);
  const redraft = async () => {
    setBusy(true); setError('');
    try {
      const next = await draftOutreach(d.source_kind, d.source_id);
      void client.invalidateQueries({ queryKey: ['evi-queues'] });
      onNavigate(`/property-outreach/${next.id}`);
    } catch (err) { setError(await getDisplayErrorMessage(err, 'The draft was not made.')); } finally { setBusy(false); }
  };
  return (
    <div className={styles.page}>
      <p className={styles.crumb}><a href={back.path} onClick={(e) => { e.preventDefault(); onNavigate(back.path); }}>{back.label}</a></p>
      <h1 className={styles.heading}>Contact the property: {d.location.name || 'this location'}</h1>
      <section className={styles.card} aria-label="The draft">
        {d.status === 'draft' ? <OutreachEditor key={d.id} draft={d} onChange={update} /> : <OutreachRecord draft={d} />}
        {error && <div className={styles.alertErr} role="alert">{error}</div>}
        {d.can.redraft && <div className={styles.actions}><button type="button" className={formStyles.btnSecondary} disabled={busy} onClick={() => void redraft()}>{busy ? 'Drafting' : redraftLabel(d)}</button></div>}
      </section>
    </div>
  );
}
